import { DANGER_WINDOW_FRAC, LANE_GAP_PX } from './pianoRenderConstants';
import type { PianoLayout } from '../pianoPlayfieldGeometry';
import type { PianoRenderInput } from './pianoRenderTypes';
import { drawHoldTile, drawTile } from './pianoTileShapes';
import type { RenderQuality } from '../../rendering/renderQuality';

/**
 * Owns the visible-window cursor for the tile iteration. Pulled out
 * of the orchestrator so the per-chart "first event we might still
 * have to draw" pointer survives across frames — on a 600-event
 * chart at 60 fps that turns ~36 k full-array iterations per second
 * into ~600 ish (the actually visible window plus a small forward
 * margin), which is the dominant per-frame win for long Piano maps
 * on phones.
 *
 * Correctness invariant: events are stored sorted by `ev.beat`
 * ascending, so the cursor only advances when an event is
 * provably-permanently past — once `max(beat, holdEndBeat) <
 * lowerBound`, the inner draw loop wouldn't have drawn it on any
 * future frame anyway. Inner per-event filters still run for
 * transiently-past events (resolved taps inside the visible
 * window) so the cull conditions stay readable in one place.
 */
export class TileRenderer {
  private firstVisibleIdx = 0;

  /**
   * Reset the cursor on loop boundaries / chart swaps so a new
   * playthrough doesn't inherit a cursor pointing past loop N's
   * tail when loop N+1 plays from beat 0 again.
   */
  reset(): void {
    this.firstVisibleIdx = 0;
  }

  /**
   * Tile painting iterator. Walks the lane events for the current
   * loop, culls anything outside the visible approach window, and
   * dispatches each visible event to {@link drawTile} (tap) or
   * {@link drawHoldTile} (hold). The orchestrator threads in its
   * `reducedMotion` flag so the shadowBlur passes can skip on
   * accessibility setups.
   */
  draw(
    ctx: CanvasRenderingContext2D,
    input: PianoRenderInput,
    layout: PianoLayout,
    reducedMotion: boolean,
    quality: RenderQuality,
  ): void {
    // On low-tier devices the shadow-blur passes inside drawTile /
    // drawHoldTile are by far the most expensive per-tile cost — a
    // ~20-tile frame can easily spend more time on shadows than on
    // the rest of the scene combined. The shadow path is gated on this
    // flag; per-tile animations (inner glow, hold chevrons) stay live
    // on low tier so the visual vocabulary doesn't degrade — those
    // remain gated on `reducedMotion` inside the shape primitives.
    const lightweightTiles = reducedMotion || !quality.tileShadows;
    const { chart, beat, state, laneEvents, perfectOverlapBeats } = input;
    const dangerWindowBeats = perfectOverlapBeats * DANGER_WINDOW_FRAC;
    const palette = chart.palette;
    const beatsPerMs = chart.bpm / 60_000;
    const approachBeats = chart.approachMs * beatsPerMs;
    const upperBound = beat + approachBeats * 1.05;
    // Keep unresolved tiles on-screen until just past the engine's
    // perfect-overlap cutoff so the player can see the tile actually
    // reach the bar's trailing edge before it auto-misses. The 1.05×
    // buffer gives a frame of slack so a tile resolved on the cutoff
    // frame doesn't pop out before its verdict text spawns.
    const lowerBound = beat - perfectOverlapBeats * 1.05;

    // Advance the visible-window cursor past events whose latest
    // possible draw beat (max of `ev.beat` for taps, `holdEndBeat`
    // for holds) is already behind `lowerBound`. Sorted-by-beat
    // input means the cursor is monotonic and the work-per-frame
    // collapses to "events near the current beat" instead of "every
    // event in the chart". A held but unresolved hold whose tail
    // sits far ahead of `lowerBound` stops the cursor at the right
    // place and the inner loop's transient-skip logic continues to
    // drop nearby resolved taps the cursor can't safely advance
    // over yet.
    while (this.firstVisibleIdx < laneEvents.length) {
      const ev = laneEvents[this.firstVisibleIdx];
      const isHold = ev.holdEndBeat !== undefined;
      const visibleBeat = isHold ? Math.max(ev.beat, ev.holdEndBeat!) : ev.beat;
      if (visibleBeat < lowerBound) {
        this.firstVisibleIdx += 1;
      } else {
        break;
      }
    }

    for (let i = this.firstVisibleIdx; i < laneEvents.length; i += 1) {
      const ev = laneEvents[i];
      // A fully-released hold (both head + release resolved) is gone
      // for good. Tap-only resolved notes also drop out here.
      if (state.resolvedReleaseIds.has(ev.id)) {
        continue;
      }
      const isHold = ev.holdEndBeat !== undefined;
      const headDone = state.resolvedIds.has(ev.id);
      // Drop tap notes whose head has been resolved (verdict floated
      // away, nothing left to draw). For holds, also drop the ribbon
      // when the head is resolved but the lane ISN'T currently held —
      // i.e. the head verdict was `miss`, so engine never inserted
      // into heldLanes and there's no live hold to render. Without
      // this gate the missed-head ribbon would render dim + anchored
      // at the hit line for the remaining `holdEndBeat - beat` beats,
      // contradicting the "Miss" verdict label.
      if (headDone && (!isHold || !state.heldLanes.has(ev.lane))) {
        continue;
      }
      // Bound check uses the LATER of (head beat, tail beat) so a
      // hold whose head passed lower-bound is still kept on-screen
      // until its tail finishes.
      const visibleBeat = isHold ? Math.max(ev.beat, ev.holdEndBeat!) : ev.beat;
      if (visibleBeat < lowerBound) {
        continue;
      }
      if (ev.beat > upperBound) {
        break;
      }
      const x = layout.lane0X + ev.lane * (layout.laneWidth + LANE_GAP_PX);
      const isDual = ev.kind === 'dual';
      const baseColour = isDual ? palette.padPerfect : palette.pad;
      // Fade window in CSS px — tiles ramp from alpha 0 to alpha 1 as
      // their leading edge descends from y=0 (just spawning at the top
      // of the visible region) down to y=fadeRange. Tied to tileHeight
      // so taller tiles get a proportionally longer fade.
      const fadeRange = layout.tileHeight * 0.8;

      if (isHold) {
        const tailY = (1 - (ev.holdEndBeat! - beat) / approachBeats) * layout.hitLineY;
        // headY clamps at hitLineY once the player engages the hold —
        // the tail body should anchor at the hit line and shrink upward
        // as time passes, never extend past it.
        const headY = headDone
          ? layout.hitLineY
          : (1 - (ev.beat - beat) / approachBeats) * layout.hitLineY;
        // headDone implies heldLanes.has(ev.lane) at this point — the
        // missed-head case was filtered above — but keep the gate so a
        // future change to the head filter doesn't silently turn missed-
        // head ribbons into "held active" visuals.
        const heldActive = headDone && state.heldLanes.has(ev.lane);
        // Danger only applies to the head before it's been tapped — the
        // body itself can't be "missed", and the release window is
        // signalled differently (tail near hit line + glow). Pulse fires
        // in the LATE half of the perfect-overlap window, when the tile
        // is sliding off the bar and the player is about to lose the
        // chance to tap it.
        let dangerPulse = 0;
        if (!headDone && dangerWindowBeats > 0) {
          const lateOffset = beat - ev.beat;
          const dangerStart = perfectOverlapBeats - dangerWindowBeats;
          if (lateOffset > dangerStart && lateOffset < perfectOverlapBeats) {
            dangerPulse = 0.5 + 0.5 * Math.sin(performance.now() * 0.035);
          }
        }
        const headProgress = headDone ? 1 : 1 - (ev.beat - beat) / approachBeats;
        const anticipation = headDone ? 0 : Math.max(0, (headProgress - 0.75) / 0.25);
        const entryAlpha = headDone ? 1 : Math.max(0, Math.min(1, headY / fadeRange));
        ctx.save();
        ctx.globalAlpha = entryAlpha;
        drawHoldTile(
          ctx,
          x,
          headY,
          tailY,
          layout.laneWidth,
          baseColour,
          palette,
          dangerPulse,
          heldActive,
          headDone,
          isDual,
          anticipation,
          reducedMotion,
          lightweightTiles,
        );
        ctx.restore();
        continue;
      }

      const beatsUntilHit = ev.beat - beat;
      const progress = 1 - beatsUntilHit / approachBeats;
      const y = progress * layout.hitLineY;
      const lateOffset = beat - ev.beat;
      // Pulse fires once the tile has passed the bar's centre on its
      // way off the line — see the hold-tile branch above for the same
      // window calculation.
      const dangerStart = perfectOverlapBeats - dangerWindowBeats;
      const inDanger =
        dangerWindowBeats > 0 &&
        lateOffset > dangerStart &&
        lateOffset < perfectOverlapBeats;
      const dangerPulse = inDanger ? 0.5 + 0.5 * Math.sin(performance.now() * 0.035) : 0;
      const anticipation = Math.max(0, (progress - 0.75) / 0.25);
      const entryAlpha = Math.max(0, Math.min(1, y / fadeRange));
      ctx.save();
      ctx.globalAlpha = entryAlpha;
      drawTile(
        ctx,
        x,
        y,
        layout.laneWidth,
        layout.tileHeight,
        baseColour,
        palette,
        dangerPulse,
        isDual,
        anticipation,
        reducedMotion,
        lightweightTiles,
      );
      ctx.restore();
    }
  }
}
